import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle, CircleDashed, Warning, Clock, ShieldCheck, Question, Pulse, Database, Buildings as BuildingsIcon } from "@phosphor-icons/react";

const STATUS_META = {
  done:        { icon: CheckCircle,  cls: "text-[var(--span-success)]",  label: "Done" },
  in_progress: { icon: Clock,        cls: "text-[var(--span-info)]",     label: "In progress" },
  blocked:     { icon: Warning,      cls: "text-[var(--span-danger)]",   label: "Blocked" },
  todo:        { icon: CircleDashed, cls: "text-[var(--span-text-2)]",   label: "Todo" },
};

export default function BuildStatus() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/admin/build-status").then(({ data }) => setData(data));
  useEffect(() => { load(); const t = setInterval(load, 60_000); return () => clearInterval(t); }, []);

  const toggleVerify = async (gap) => {
    const notes = window.prompt("Optional CEO note (will be saved to audit log):", gap.ceo_notes || "");
    if (notes === null) return;
    await api.post(`/admin/build-status/${encodeURIComponent(gap.gap_id)}/verify`, {
      ceo_verified: !gap.ceo_verified,
      ceo_notes: notes,
    });
    load();
  };

  if (!data) return <div className="text-[12px] text-[var(--span-text-2)]" data-testid="build-status-loading">Loading build status…</div>;

  const filtered = filter === "all" ? data.features : data.features.filter((f) => f.status === filter);

  return (
    <div className="space-y-5" data-testid="build-status-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">CEO Oversight</div>
          <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Build Status</h1>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1">
            Real-time tracking of all 18 platform gaps from the Gap Analysis · refreshes every 60s
          </div>
        </div>
        <div className="text-right">
          <div className="font-display font-bold text-[32px] tnum">{data.summary.percent_done}%</div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--span-text-2)]">{data.summary.done} of {data.summary.total} done · {data.summary.ceo_verified} CEO-verified</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="hairline surface p-1 h-3 flex overflow-hidden">
        <div className="bg-[var(--span-success)] h-full" style={{ width: `${(data.summary.done / data.summary.total) * 100}%` }} title={`${data.summary.done} done`}/>
        <div className="bg-[var(--span-info)] h-full" style={{ width: `${(data.summary.in_progress / data.summary.total) * 100}%` }} title={`${data.summary.in_progress} in progress`}/>
        <div className="bg-[var(--span-danger)] h-full" style={{ width: `${(data.summary.blocked / data.summary.total) * 100}%` }} title={`${data.summary.blocked} blocked`}/>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-1.5">
        {["all", "done", "in_progress", "blocked", "todo"].map((s) => (
          <button key={s} data-testid={`filter-${s}`} onClick={() => setFilter(s)} className={`badge ${filter === s ? "badge-success" : ""}`}>
            {s.replace("_", " ")} ({s === "all" ? data.summary.total : data.summary[s] || 0})
          </button>
        ))}
      </div>

      {/* Gap tracker table */}
      <div className="card-flat" data-testid="gap-tracker">
        <table className="span-table">
          <thead>
            <tr><th>Gap</th><th>Title</th><th>Track</th><th>Wk</th><th>Status</th><th>Owner</th><th>CEO ✓</th><th>Notes</th></tr>
          </thead>
          <tbody>
            {filtered.map((g) => {
              const meta = STATUS_META[g.status] || STATUS_META.todo;
              const Icon = meta.icon;
              return (
                <tr key={g.gap_id} data-testid={`gap-row-${g.gap_id}`}>
                  <td className="font-mono font-bold">{g.gap_id}</td>
                  <td className="font-medium">{g.title}</td>
                  <td className="text-[11px] text-[var(--span-text-2)]">{g.track}</td>
                  <td className="font-mono text-[11px]">{g.week}</td>
                  <td>
                    <span className={`flex items-center gap-1 text-[11px] ${meta.cls}`}>
                      <Icon size={12}/> {meta.label}
                    </span>
                  </td>
                  <td className="text-[11px]">{g.owner}</td>
                  <td>
                    <button data-testid={`verify-${g.gap_id}`} onClick={() => toggleVerify(g)} className={`badge ${g.ceo_verified ? "badge-success" : ""}`}>
                      {g.ceo_verified ? "✓ verified" : "verify"}
                    </button>
                  </td>
                  <td className="text-[11px] text-[var(--span-text-2)] max-w-md">
                    {g.notes}
                    {g.ceo_notes && <div className="font-mono text-[10px] mt-0.5 italic">CEO: {g.ceo_notes}</div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <EnvHealth env={data.env}/>
        <LiveData live={data.live_data}/>
      </div>

      <CEOQuestions questions={data.ceo_questions}/>

      <div className="text-[10px] font-mono text-[var(--span-text-2)] text-right">
        Last refreshed: {new Date(data.generated_at).toLocaleString()}
      </div>
    </div>
  );
}

function EnvHealth({ env }) {
  const labels = {
    email_resend: "Email · Resend", stripe_mode: "Stripe", polygon_anchor: "Polygon",
    twilio_whatsapp: "Twilio WhatsApp", vapid_web_push: "VAPID Web Push",
    field_encryption: "Field encryption", llm_emergent_key: "LLM (Emergent)",
    mongo: "MongoDB", scheduler: "APScheduler",
  };
  return (
    <div className="card-flat p-5" data-testid="env-health">
      <div className="flex items-center gap-2 mb-3">
        <Pulse size={16} weight="duotone"/>
        <div className="font-display font-bold text-[14px]">Environment health</div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-[12px]">
        {Object.entries(env).map(([k, v]) => {
          const isGood = ["configured", "ok", "real", "live"].includes(v);
          const isMissing = ["missing", "blocked"].includes(v);
          return (
            <div key={k} className="hairline px-2 py-1.5 surface flex items-center justify-between">
              <span className="text-[var(--span-text-2)]">{labels[k] || k}</span>
              <span className={`font-mono text-[10px] ${isGood ? "text-[var(--span-success)]" : isMissing ? "text-[var(--span-danger)]" : "text-[var(--span-text-2)]"}`}>{v}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LiveData({ live }) {
  return (
    <div className="card-flat p-5" data-testid="live-data">
      <div className="flex items-center gap-2 mb-3">
        <Database size={16} weight="duotone"/>
        <div className="font-display font-bold text-[14px]">Live data counts</div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-[12px]">
        {Object.entries(live).map(([k, v]) => (
          <div key={k} className="hairline px-2 py-1.5 surface flex items-center justify-between">
            <span className="text-[var(--span-text-2)] capitalize">{k.replace("_", " ")}</span>
            <span className="font-mono tnum font-bold">{v?.toLocaleString?.() ?? v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CEOQuestions({ questions }) {
  const answered = questions.filter(q => q[2] === "answered").length;
  const pending = questions.length - answered;
  return (
    <div className="card-flat p-5" data-testid="ceo-questions">
      <div className="flex items-center gap-2 mb-2">
        <Question size={16} weight="duotone"/>
        <div className="font-display font-bold text-[14px]">CEO strategic questions</div>
        <span className="ml-auto text-[10px] font-mono">
          <span className="text-emerald-600">{answered} answered</span>
          {" · "}
          <span className="text-amber-600">{pending} pending</span>
        </span>
      </div>
      <div className="text-[12px] text-[var(--span-text-2)] mb-3">
        Strategic decisions from the build prompt — the CEO has now resolved {answered} of {questions.length}.
      </div>
      <ul className="space-y-2">
        {questions.map((row) => {
          const [id, q, status, answer] = row;
          const isAnswered = status === "answered";
          return (
            <li key={id} className="hairline surface p-2.5" data-testid={`ceo-question-${id}`}>
              <div className="flex items-start gap-2 text-[12px]">
                <span className="font-mono font-bold text-[10px] mt-0.5 w-12 shrink-0">{id}</span>
                <span className="flex-1">{q}</span>
                <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 ${isAnswered ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                  {status || "pending"}
                </span>
              </div>
              {answer && (
                <div className={`mt-1.5 ml-14 text-[11px] ${isAnswered ? "text-emerald-700" : "text-amber-700"}`}>
                  → {answer}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
